import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, catchError, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'

import {
  CloseSessionBody,
  CommitteeLeasesPage,
  CommitteeSessionDetail,
  CommitteeSessionListItem,
  CommitteeSummary,
  CreateSessionDto,
  OpenCommitteeSessionResult,
  SessionNotificationResult,
  SessionStatus,
} from '@modules/private/committee/data/interfaces/committee-session.interface'
import { Committee, CommitteeMember, CommitteePresentationFile } from '@modules/private/lease/data/interfaces/committee.interface'
import { LeaseGroup, LeaseLens } from '@modules/private/committee/data/constants/committee.constants'
import { extractApiErrors } from '@data/utils/api-error.util'
import { environment } from '@env/environment'

/** El sobre `{ error, msg, data }` con el que responde toda la API. */
export type Wrapped<T> = { error: boolean; msg: string; data: T | null }

/**
 * Todo `/committee/**` en un solo sitio (D-10).
 *
 * Lo consumen dos módulos —la pestaña Comité del expediente (`lease/`) y la pantalla `/comites`—, y
 * por eso vive en `data/services/modules/` y no dentro de uno de ellos (`docs/standards.md` § dónde
 * va cada cosa). Antes estos métodos estaban en `ApiLeaseService`, que además arrastraba cuatro
 * legados sin consumidor (`createCommitteeMeeting`, `ensureCommitteeForLease`, `getCommitteeHistory`,
 * `updateCommitteeLegacy`); dos servicios para el mismo recurso era la ambigüedad que se quiso evitar.
 *
 * **No extiende `ApiClass` a propósito**: su `this.path` mutable es el riesgo documentado en
 * `docs/architecture/api-layer.md` y `ApiLeaseService` es su peor caso. Aquí cada método arma su URL.
 *
 * **Contrato L de `standards.md`:** ningún método lanza. El error viaja en `{ error: true, msg }` y el
 * `msg` sale de `extractApiErrors`, que saca el detalle de validación en español en vez del genérico
 * «Bad Request».
 */
@Injectable({ providedIn: 'root' })
export class ApiCommitteeService {
  private readonly http = inject(HttpClient)
  private readonly base = `${environment.apiUrl}committee`

  // ── Sesiones (F-142) ──

  getSessions(status: SessionStatus): Observable<Wrapped<CommitteeSessionListItem[]>> {
    return this.wrap(this.http.get(`${this.base}/sessions`, { params: { status } }), 'No se pudieron cargar las sesiones del comité')
  }

  getSession(id: string): Observable<Wrapped<CommitteeSessionDetail>> {
    return this.wrap(this.http.get(`${this.base}/sessions/${id}`), 'No se pudo cargar la sesión del comité')
  }

  getSummary(): Observable<Wrapped<CommitteeSummary>> {
    return this.wrap(this.http.get(`${this.base}/sessions/summary`), 'No se pudo cargar el resumen del comité')
  }

  /**
   * Los arrendamientos de un grupo, con su estado de comité resuelto. `q` busca por folio o cliente;
   * la lente «sin comité» la aplica el servidor, que es el único que puede contar TODA la cartera.
   */
  getLeases(group: LeaseGroup, lens: LeaseLens, page: number, limit: number, q = ''): Observable<Wrapped<CommitteeLeasesPage>> {
    let params = new HttpParams().set('group', group).set('lens', lens).set('page', String(page)).set('limit', String(limit))
    if (q.trim()) params = params.set('q', q.trim())

    return this.wrap(this.http.get(`${this.base}/sessions/leases`, { params }), 'No se pudieron cargar los arrendamientos')
  }

  /** Responde la sesión creada Y el resultado del aviso por correo: un rechazo de SendGrid no aborta, se reporta. */
  createSession(dto: CreateSessionDto): Observable<Wrapped<OpenCommitteeSessionResult>> {
    return this.wrap(this.http.post(`${this.base}/sessions`, dto), 'No se pudo crear la sesión del comité')
  }

  //* Las escrituras de sesión devuelven el detalle recalculado —el mismo que `GET /sessions/:id`— desde api
  //* `22933080`: quien escribe lo pinta y no vuelve a pedir la sesión.
  /** Sin `decisions` cierra por votos; con ellas (reuniones sin votos con quórum) manda el dictamen manual y su `reason`. */
  closeSession(id: string, body: CloseSessionBody = {}): Observable<Wrapped<CommitteeSessionDetail>> {
    return this.wrap(this.http.post(`${this.base}/sessions/${id}/close`, body), 'No se pudo cerrar la sesión')
  }

  /** Unifica `sessionIds` (abiertas, de votación) en la sesión `id`, que es la que queda; devuelve su detalle recalculado. */
  mergeSessions(id: string, sessionIds: string[]): Observable<Wrapped<CommitteeSessionDetail>> {
    return this.wrap(this.http.post(`${this.base}/sessions/${id}/merge`, { sessionIds }), 'No se pudieron unificar las sesiones')
  }

  addSessionMinute(id: string, minute: { file: string; name?: string }): Observable<Wrapped<CommitteeSessionDetail>> {
    return this.wrap(this.http.post(`${this.base}/sessions/${id}/minutes`, minute), 'No se pudo agregar el acta')
  }

  removeSessionMinute(id: string, minuteId: string): Observable<Wrapped<CommitteeSessionDetail>> {
    return this.wrap(this.http.delete(`${this.base}/sessions/${id}/minutes/${minuteId}`), 'No se pudo quitar el acta')
  }

  /** La nota de Legal/Admin sobre UN arrendamiento de la sesión (D-6). Solo con la reunión abierta. */
  updateSessionItem(id: string, meetingId: string, body: { generalComments: string }): Observable<Wrapped<{ meetingId: string; generalComments: string }>> {
    return this.wrap(this.http.patch(`${this.base}/sessions/${id}/items/${meetingId}`, body), 'No se pudo guardar la observación')
  }

  updateSessionVoters(id: string, body: { selectedVoters: string[] }): Observable<Wrapped<CommitteeSessionDetail>> {
    return this.wrap(this.http.patch(`${this.base}/sessions/${id}/voters`, body), 'No se pudieron actualizar los votantes')
  }

  /** Reenvía el correo solo a quien todavía tiene votos pendientes. */
  notifySession(id: string): Observable<Wrapped<SessionNotificationResult>> {
    return this.wrap(this.http.post(`${this.base}/sessions/${id}/notify`, {}), 'No se pudo reenviar el aviso')
  }

  // ── Por reunión (ADR-0013) — los que la pestaña Comité del expediente sigue usando ──

  /** Miembros elegibles del comité (Admin con `isCommitteeMember`). */
  /** F-165 · Desde el módulo `committee` (`view`): con `users/committee-members` (`users.get-all`) Seller recibía 403. */
  getCommitteeMembers(): Observable<Wrapped<CommitteeMember[]>> {
    return this.wrap(this.http.get(`${this.base}/members`), 'No se pudieron cargar los miembros del comité')
  }

  // ── Presentación a Comité del arrendamiento (F-165) ──

  getLeasePresentation(leaseId: string): Observable<Wrapped<CommitteePresentationFile[]>> {
    return this.wrap(this.http.get(`${this.base}/for-lease/${leaseId}/presentation`), 'No se pudo cargar la Presentación a Comité')
  }

  /** Registra un PDF que `app-input-single-file` ya subió al bucket. Responde las vigentes. */
  addLeasePresentation(leaseId: string, data: { url: string; name?: string }): Observable<Wrapped<CommitteePresentationFile[]>> {
    return this.wrap(this.http.post(`${this.base}/for-lease/${leaseId}/presentation`, data), 'No se pudo agregar la presentación')
  }

  /** Quitar conserva el archivo y deja quién y cuándo (D10). Responde las vigentes. */
  removeLeasePresentation(leaseId: string, fileId: string): Observable<Wrapped<CommitteePresentationFile[]>> {
    return this.wrap(this.http.delete(`${this.base}/for-lease/${leaseId}/presentation/${fileId}`), 'No se pudo quitar la presentación')
  }

  /** Emitir voto (Admin miembro). Funciona igual con la reunión dentro de una sesión (D-5). */
  castCommitteeVote(committeeId: string, meetingId: string, data: { decision: string; memberComments?: string }): Observable<Wrapped<Committee>> {
    return this.wrap(this.http.post(`${this.base}/${committeeId}/meetings/${meetingId}/vote`, data), 'No se pudo emitir el voto')
  }

  /** Editar el voto propio. */
  updateCommitteeVote(committeeId: string, meetingId: string, data: { decision: string; memberComments?: string }): Observable<Wrapped<Committee>> {
    return this.wrap(this.http.patch(`${this.base}/${committeeId}/meetings/${meetingId}/vote`, data), 'No se pudo actualizar el voto')
  }

  /** Editar votantes de una reunión suelta. Con `session` el backend responde 409: se hace desde la sesión. */
  updateMeetingVoters(committeeId: string, meetingId: string, data: { selectedVoters: string[] }): Observable<Wrapped<Committee>> {
    return this.wrap(this.http.patch(`${this.base}/${committeeId}/meetings/${meetingId}`, data), 'No se pudieron actualizar los votantes')
  }

  /** Cerrar una reunión suelta. Con `session` el backend responde 409: se cierra la sesión. */
  closeCommitteeMeeting(committeeId: string, meetingId: string, data: { generalComments?: string }): Observable<Wrapped<Committee>> {
    return this.wrap(this.http.post(`${this.base}/${committeeId}/meetings/${meetingId}/close`, data), 'No se pudo cerrar la reunión')
  }

  addCommitteeMinute(committeeId: string, meetingId: string, data: { file: string; name?: string }): Observable<Wrapped<Committee>> {
    return this.wrap(this.http.post(`${this.base}/${committeeId}/meetings/${meetingId}/minutes`, data), 'No se pudo agregar el acta')
  }

  removeCommitteeMinute(committeeId: string, meetingId: string, minuteId: string): Observable<Wrapped<Committee>> {
    return this.wrap(this.http.delete(`${this.base}/${committeeId}/meetings/${meetingId}/minutes/${minuteId}`), 'No se pudo quitar el acta')
  }

  /**
   * Convierte la respuesta cruda al sobre y **atrapa el error sin lanzar**. El `msg` de un fallo sale
   * de `extractApiErrors` sobre el cuerpo que mandó la API (`e.error`), que es donde viajan los
   * mensajes de validación en español.
   */
  private wrap<T>(source: Observable<unknown>, fallback: string): Observable<Wrapped<T>> {
    return source.pipe(
      map((r) => r as Wrapped<T>),
      catchError((e) => of({ error: true, msg: extractApiErrors(e?.error, fallback).join(' · '), data: null })),
    )
  }
}
